// What needs chasing, decided in one place so the Follow-ups page and the nav
// badge can't disagree. Each quote lands in at most one bucket — the most
// urgent one it qualifies for — so the list never nags twice about one job.

import { STAGE, stageOf, isPaid, balanceCents, jobEndMs } from "./jobs";
import { daysSince } from "./contact";

export const LOST_REASONS = [
  "Went with someone else",
  "Date not available",
  "Too expensive",
  "Stopped responding",
  "Not a fit",
];

export const RULES = {
  STALE_QUOTE_DAYS: 3, // sent this long with no payment → nudge
  BALANCE_WINDOW_DAYS: 7, // balance falls due within this many days
  EXPIRING_DAYS: 3, // valid-until within this many days (or already past)
  STALE_DRAFT_DAYS: 1, // built but never sent
};

const DAY = 86400000;

/** The balance is due the day before the event (Eastern-day accuracy isn't
 * needed here — this only decides whether it shows in a 7-day window). */
export function balanceDueMs(job) {
  if (!job.event_date) return 0;
  const t = new Date(job.event_date).getTime();
  return Number.isNaN(t) ? 0 : t - DAY;
}

function validUntilMs(job) {
  if (!job.valid_until) return 0;
  const t = new Date(`${String(job.valid_until).slice(0, 10)}T23:59:59`).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** A new lead still waiting on you: never answered, or they've replied since. */
export function needsReply(lead) {
  if (!lead.first_reply_at) return true;
  return Boolean(lead.replied_at && new Date(lead.replied_at) >= new Date(lead.first_reply_at));
}

export function buildFollowups(quotes = [], leads = [], now = Date.now()) {
  const newLeads = leads
    .filter((l) => l.stage === "new" && needsReply(l))
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  // Quotes the no-reply emails are already chasing don't need a check-in from you too.
  const chased = new Set(leads.filter((l) => l.quote_id && l.campaign?.status === "active").map((l) => l.quote_id));

  const balances = [];
  const expiring = [];
  const stale = [];
  const drafts = [];

  for (const q of quotes) {
    const stage = stageOf(q, now);
    if (stage === STAGE.LOST || q.status === "declined") continue;

    // Booked but still owing — the money that's most at risk, so it wins.
    if (isPaid(q) && balanceCents(q) > 0) {
      const due = balanceDueMs(q);
      if (due && due - now <= RULES.BALANCE_WINDOW_DAYS * DAY) {
        balances.push({ ...q, _dueMs: due, _overdue: due < now });
      }
      continue;
    }
    if (isPaid(q)) continue; // settled

    if (stage === STAGE.DRAFT) {
      if (q.send_requested_at) continue; // waiting for your approval: its own list
      const age = daysSince(q.created_at, now);
      if (age !== null && age >= RULES.STALE_DRAFT_DAYS) drafts.push({ ...q, _age: age });
      continue;
    }

    // Sent and unpaid from here on.
    const eventPassed = Boolean(jobEndMs(q)) && jobEndMs(q) < now;
    const until = validUntilMs(q);
    if (until && until - now <= RULES.EXPIRING_DAYS * DAY) {
      expiring.push({ ...q, _untilMs: until, _expired: until < now, _eventPassed: eventPassed });
      continue;
    }
    const sentAge = daysSince(q.sent_at, now);
    if (chased.has(q.id) && !eventPassed) continue;
    if (eventPassed || (sentAge !== null && sentAge >= RULES.STALE_QUOTE_DAYS)) {
      stale.push({ ...q, _sentAge: sentAge, _eventPassed: eventPassed });
    }
  }

  balances.sort((a, b) => a._dueMs - b._dueMs);
  expiring.sort((a, b) => a._untilMs - b._untilMs);
  stale.sort((a, b) => (b._sentAge ?? 0) - (a._sentAge ?? 0));
  drafts.sort((a, b) => b._age - a._age);

  return {
    newLeads,
    balances,
    expiring,
    stale,
    drafts,
    count: newLeads.length + balances.length + expiring.length + stale.length + drafts.length,
  };
}
